import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { TranslateService } from '@ngx-translate/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { PermitStatusService } from '../../services/permit-status.service';
import { PermitCategoryService } from '../../services/permit-category.service';
import { PermitTagService } from '../../services/permit-tag.service';
import { PermitDepartmentService } from '../../services/permit-department.service';
import { PermitDepartment, permitDepartmentName } from '../../models/permit-department.model';
import { PermitStatus } from '../../models/permit-status.model';
import { PermitCategory, I18nString } from '../../models/permit-category.model';
import { PermitTypeSummary } from '../../models/permit-type.model';
import { AdminPermitFilters, SavedPermitFilter } from '../../models/permit-filters.model';
import { SyncStatus } from '../../constants/permit.constants';

@Component({
    selector: 'app-permit-filter-sidenav',
    templateUrl: './permit-filter-sidenav.component.html',
    styleUrls: ['./permit-filter-sidenav.component.scss'],
})
export class PermitFilterSidenavComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private _triggerFilterChange = new Subject<void>();
    // Guards against MatDatepicker CVA writeValue→onChange re-emitting valueChanges synchronously during batch patch
    private _patchInProgress = false;

    @Output() filterChanged = new EventEmitter<Partial<AdminPermitFilters>>();

    @Input() savedFilters: SavedPermitFilter[] = [];
    @Output() saveFilter = new EventEmitter<{ name: string; filters: AdminPermitFilters }>();
    @Output() deleteFilter = new EventEmitter<string>();
    @Output() loadFilter = new EventEmitter<SavedPermitFilter>();

    @ViewChild('filterNameInput') filterNameInput: ElementRef<HTMLInputElement>;

    isSaving = false;
    isAccordionOpen = false;
    maxDate = new Date();
    filterNameControl = new FormControl('');

    filterForm: FormGroup;

    statuses: PermitStatus[] = [];
    categories: PermitCategory[] = [];
    permitTypes: PermitTypeSummary[] = [];
    departments: PermitDepartment[] = [];
    tagSuggestions: string[] = [];
    selectedTags: string[] = [];

    syncStatusOptions = [
        { value: SyncStatus.SYNCED, labelKey: 'permit.manage.list.sync_synced' },
        { value: SyncStatus.SYNC_PENDING, labelKey: 'permit.manage.list.sync_pending' },
        { value: SyncStatus.SYNC_ERROR, labelKey: 'permit.manage.list.sync_error' },
    ];

    separatorKeyCodes: number[] = [ENTER, COMMA];

    tagInputCtrl = new FormControl('');

    constructor(
        private fb: FormBuilder,
        private statusService: PermitStatusService,
        private categoryService: PermitCategoryService,
        private tagService: PermitTagService,
        private departmentService: PermitDepartmentService,
        private translate: TranslateService,
    ) {
        this.filterForm = this.fb.group({
            status: [null],
            category: [null],
            permitType: [{ value: null, disabled: true }],
            department: [null],
            assignedTo: [null],
            dateFrom: [null],
            dateTo: [null],
            syncStatus: [null],
        });
    }

    ngOnInit(): void {
        this.statusService
            .getAllStatuses()
            .pipe(takeUntil(this.destroy$))
            .subscribe((statuses) => {
                this.statuses = statuses;
            });

        this.categoryService
            .getCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe((categories) => {
                this.categories = categories;
            });

        this.tagService
            .getTags()
            .pipe(takeUntil(this.destroy$))
            .subscribe((tags) => {
                this.tagSuggestions = tags;
            });

        this.departmentService
            .getDepartments()
            .pipe(takeUntil(this.destroy$))
            .subscribe((departments) => {
                this.departments = departments || [];
            });

        // Debounced filter change
        this._triggerFilterChange.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
            this._emitFilters();
        });

        // Watch all controls for changes
        const controls = ['status', 'department', 'assignedTo', 'dateFrom', 'dateTo', 'syncStatus'];
        controls.forEach((ctrlName) => {
            this.filterForm
                .get(ctrlName)
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    if (this._patchInProgress) return;
                    this._triggerFilterChange.next();
                });
        });

        // Category change: reset permitType, load types for selected category
        this.filterForm
            .get('category')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((categoryId) => {
                const permitTypeCtrl = this.filterForm.get('permitType');
                permitTypeCtrl.setValue(null, { emitEvent: false });
                if (categoryId) {
                    const cat = this.categories.find((c) => c._id === categoryId);
                    this.permitTypes = cat ? cat.permitTypes || [] : [];
                    permitTypeCtrl.enable({ emitEvent: false });
                } else {
                    this.permitTypes = [];
                    permitTypeCtrl.disable({ emitEvent: false });
                }
                if (this._patchInProgress) return;
                this._triggerFilterChange.next();
            });

        // permitType change
        this.filterForm
            .get('permitType')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this._patchInProgress) return;
                this._triggerFilterChange.next();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Public method called by host to reset all filters */
    reset(): void {
        this.filterForm.reset({
            status: null,
            category: null,
            permitType: null,
            department: null,
            assignedTo: null,
            dateFrom: null,
            dateTo: null,
            syncStatus: null,
        });
        this.filterForm.get('permitType').disable({ emitEvent: false });
        this.selectedTags = [];
        this.permitTypes = [];
        this.tagInputCtrl.setValue(null, { emitEvent: false });
        this._triggerFilterChange.next();
    }

    /** Add a tag (called from template chip input or test) */
    addTag(tag: string): void {
        const trimmed = tag.trim();
        if (trimmed && !this.selectedTags.includes(trimmed)) {
            this.selectedTags = [...this.selectedTags, trimmed];
            this._triggerFilterChange.next();
        }
    }

    removeTag(tag: string): void {
        this.selectedTags = this.selectedTags.filter((t) => t !== tag);
        this._triggerFilterChange.next();
    }

    onTagInputTokenEnd(event: any): void {
        const value = (event.value || '').trim();
        if (value) {
            this.addTag(value);
        }
        if (event.chipInput) {
            event.chipInput.clear();
        }
        this.tagInputCtrl.setValue(null, { emitEvent: false });
    }

    onTagSuggestionSelected(event: any): void {
        const value = event.option.viewValue;
        this.addTag(value);
        this.tagInputCtrl.setValue(null, { emitEvent: false });
    }

    clearField(fieldName: string): void {
        if (fieldName === 'tags') {
            this.selectedTags = [];
        } else if (fieldName === 'category') {
            this.filterForm.get('category').setValue(null);
            this.filterForm.get('permitType').setValue(null, { emitEvent: false });
            this.filterForm.get('permitType').disable({ emitEvent: false });
            this.permitTypes = [];
            this._triggerFilterChange.next();
            return;
        } else {
            this.filterForm.get(fieldName)?.setValue(null);
        }
        this._triggerFilterChange.next();
    }

    toggleAccordion(): void {
        this.isAccordionOpen = !this.isAccordionOpen;
    }

    startSave(): void {
        this.isAccordionOpen = true;
        this.isSaving = true;
        this.filterNameControl.setValue('');
        setTimeout(() => {
            this.filterNameInput?.nativeElement?.focus();
        });
    }

    confirmSave(): void {
        const name = this.filterNameControl.value?.trim();
        if (!name) return;

        const filters = this.buildCurrentFilters();
        this.saveFilter.emit({ name, filters });
        this.isSaving = false;
        this.filterNameControl.setValue('');
    }

    cancelSave(): void {
        this.isSaving = false;
        this.filterNameControl.setValue('');
    }

    onLoadFilter(sf: SavedPermitFilter): void {
        this.loadFilter.emit(sf);
    }

    onDeleteFilter(id: string, event: Event): void {
        event.stopPropagation();
        this.deleteFilter.emit(id);
    }

    /** Public method called by host to reflect a loaded/restored filter set. Does NOT emit filterChanged. */
    patchFormWithFilters(filters: AdminPermitFilters): void {
        const f = filters || {};
        // Prevent any subscription (including MatDatepicker CVA writeValue→onChange re-entrant path) from triggering emission
        this._patchInProgress = true;

        this.filterForm.get('category').setValue(f.category ?? null, { emitEvent: false });
        const permitTypeCtrl = this.filterForm.get('permitType');
        if (f.category) {
            const cat = this.categories.find((c) => c._id === f.category);
            this.permitTypes = cat ? cat.permitTypes || [] : [];
            permitTypeCtrl.enable({ emitEvent: false });
        } else {
            this.permitTypes = [];
            permitTypeCtrl.disable({ emitEvent: false });
        }
        permitTypeCtrl.setValue(f.permitType ?? null, { emitEvent: false });

        this.filterForm.get('status').setValue(f.status ?? null, { emitEvent: false });
        this.filterForm.get('department').setValue(f.department ?? null, { emitEvent: false });
        this.filterForm.get('assignedTo').setValue(f.assignedTo ?? null, { emitEvent: false });
        this.filterForm.get('dateFrom').setValue(f.dateFrom ?? null, { emitEvent: false });
        this.filterForm.get('dateTo').setValue(f.dateTo ?? null, { emitEvent: false });
        this.filterForm.get('syncStatus').setValue(f.syncStatus ?? null, { emitEvent: false });

        this.selectedTags = f.tags ? [...f.tags] : [];
        this._patchInProgress = false;
    }

    /** Count of active filters for the badge */
    get activeFilterCount(): number {
        let count = 0;
        const formValue = this.filterForm.getRawValue();
        const simpleFields = ['status', 'category', 'permitType', 'department', 'assignedTo', 'dateFrom', 'dateTo', 'syncStatus'];
        simpleFields.forEach((f) => {
            if (formValue[f] !== null && formValue[f] !== undefined && formValue[f] !== '') {
                count += 1;
            }
        });
        if (this.selectedTags.length > 0) {
            count += 1;
        }
        return count;
    }

    departmentLabel(department: PermitDepartment): string {
        const name = permitDepartmentName(department.name, this.translate.currentLang || 'fr');
        return department.isActive ? name : `${name} ${this.translate.instant('permit.config.departments.archived_suffix')}`;
    }

    resolveI18n(value: I18nString | string): string {
        if (!value) return '';
        if (typeof value === 'string') return value;
        const lang = this.translate.currentLang;
        return value[lang] || value.fr || value.en || '';
    }

    buildCurrentFilters(): AdminPermitFilters {
        const raw = this.filterForm.getRawValue();
        const filters: AdminPermitFilters = {};

        if (raw.status) filters.status = raw.status;
        if (raw.category) filters.category = raw.category;
        if (raw.permitType) filters.permitType = raw.permitType;
        if (raw.department) filters.department = raw.department;
        if (raw.assignedTo) filters.assignedTo = raw.assignedTo;
        if (raw.dateFrom) filters.dateFrom = moment(raw.dateFrom).toISOString();
        if (raw.dateTo) filters.dateTo = moment(raw.dateTo).toISOString();
        if (raw.syncStatus) filters.syncStatus = raw.syncStatus;
        if (this.selectedTags.length > 0) filters.tags = [...this.selectedTags];

        return filters;
    }

    private _emitFilters(): void {
        this.filterChanged.emit(this.buildCurrentFilters());
    }
}
